import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, FolderKanban, Clock, Euro, Target, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { toast } from 'sonner';

export default function ProjectenPage() {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [projectStats, setProjectStats] = useState({});
  const [form, setForm] = useState({
    client_id: '',
    naam: '',
    beschrijving: '',
    uurtarief: '',
    budget_uren: '',
    actief: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes] = await Promise.all([
        api.get('/projects'),
        api.get('/clients')
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectStats = async (projectId) => {
    try {
      const res = await api.get(`/projects/${projectId}/stats`);
      setProjectStats(prev => ({ ...prev, [projectId]: res.data }));
    } catch (err) {
      console.error('Kon statistieken niet laden');
    }
  };

  const toggleExpand = (projectId) => {
    if (expandedProject === projectId) {
      setExpandedProject(null);
    } else {
      setExpandedProject(projectId);
      if (!projectStats[projectId]) {
        fetchProjectStats(projectId);
      }
    }
  };

  const openDialog = (project = null) => {
    if (project) {
      setEditing(project);
      setForm({
        client_id: project.client_id,
        naam: project.naam,
        beschrijving: project.beschrijving || '',
        uurtarief: project.uurtarief?.toString() || '',
        budget_uren: project.budget_uren?.toString() || '',
        actief: project.actief
      });
    } else {
      setEditing(null);
      setForm({
        client_id: '',
        naam: '',
        beschrijving: '',
        uurtarief: '',
        budget_uren: '',
        actief: true
      });
    }
    setShowDialog(true);
  };

  const saveProject = async () => {
    if (!form.client_id) {
      toast.error(t('projects.selectClientError'));
      return;
    }
    if (!form.naam.trim()) {
      toast.error(t('projects.nameRequired'));
      return;
    }

    try {
      const data = {
        client_id: form.client_id,
        naam: form.naam,
        beschrijving: form.beschrijving || null,
        uurtarief: form.uurtarief ? parseFloat(form.uurtarief) : null,
        budget_uren: form.budget_uren ? parseFloat(form.budget_uren) : null,
        actief: form.actief
      };

      if (editing) {
        await api.put(`/projects/${editing.project_id}`, data);
        toast.success(t('projects.projectUpdated'));
      } else {
        await api.post('/projects', data);
        toast.success(t('projects.projectSaved'));
      }
      setShowDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const deleteProject = async (project) => {
    if (!confirm(t('projects.confirmDelete', { name: project.naam }))) return;

    try {
      await api.delete(`/projects/${project.project_id}`);
      toast.success(t('projects.projectDeleted'));
      fetchData();
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projecten-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('projects.title')}</h1>
          <p className="text-muted-foreground">{t('projects.subtitle')}</p>
        </div>
        <Button onClick={() => openDialog()} data-testid="add-project-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('projects.addProject')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-cyan-100 dark:bg-cyan-950 rounded-xl">
                <FolderKanban className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('projects.activeProjects')}</p>
                <p className="text-2xl font-bold text-foreground">
                  {projects.filter(p => p.actief).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-950 rounded-xl">
                <Target className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('projects.totalProjects')}</p>
                <p className="text-2xl font-bold text-foreground">{projects.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-950 rounded-xl">
                <Clock className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('projects.withBudget')}</p>
                <p className="text-2xl font-bold text-foreground">
                  {projects.filter(p => p.budget_uren).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects List */}
      <Card className="bg-card">
        <CardHeader>
          <CardTitle className="text-foreground">{t('projects.allProjects')}</CardTitle>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t('projects.noProjects')}</p>
              <p className="text-sm">{t('projects.addFirstProject')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {projects.map(project => (
                <div 
                  key={project.project_id} 
                  className="border border-border rounded-lg overflow-hidden"
                  data-testid={`project-${project.project_id}`}
                >
                  {/* Project Row */}
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => toggleExpand(project.project_id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${project.actief ? 'bg-green-500' : 'bg-slate-400'}`} />
                      <div>
                        <h3 className="font-medium text-foreground">{project.naam}</h3>
                        <p className="text-sm text-muted-foreground">{project.client_naam}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {project.budget_uren && (
                        <span className="text-sm text-muted-foreground">
                          {t('projects.budget')}: {formatNumber(project.budget_uren)} {t('common.hours').toLowerCase()}
                        </span>
                      )}
                      {project.uurtarief && (
                        <span className="text-sm text-muted-foreground">
                          {formatCurrency(project.uurtarief)}/{t('projects.hour')}
                        </span>
                      )}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); openDialog(project); }}
                          data-testid={`edit-project-${project.project_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={(e) => { e.stopPropagation(); deleteProject(project); }}
                          data-testid={`delete-project-${project.project_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {expandedProject === project.project_id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Stats */}
                  {expandedProject === project.project_id && (
                    <div className="border-t border-border bg-muted/30 p-4">
                      {projectStats[project.project_id] ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-sm text-muted-foreground">{t('projects.totalHours')}</p>
                            <p className="text-lg font-semibold text-foreground">
                              {formatNumber(projectStats[project.project_id].total_hours)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">{t('projects.totalKilometers')}</p>
                            <p className="text-lg font-semibold text-foreground">
                              {formatNumber(projectStats[project.project_id].total_km)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">{t('projects.revenue')}</p>
                            <p className="text-lg font-semibold text-foreground">
                              {formatCurrency(projectStats[project.project_id].total_revenue)}
                            </p>
                          </div>
                          {project.budget_uren && (
                            <div>
                              <p className="text-sm text-muted-foreground">{t('projects.budgetUsed')}</p>
                              <p className="text-lg font-semibold text-foreground">
                                {formatNumber((projectStats[project.project_id].total_hours / project.budget_uren) * 100)}%
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted-foreground">
                          {t('common.loading')}
                        </div>
                      )}
                      {project.beschrijving && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <p className="text-sm text-muted-foreground">{t('common.description')}</p>
                          <p className="text-foreground">{project.beschrijving}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editing ? t('projects.editProject') : t('projects.addProject')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-foreground">{t('common.client')} *</Label>
              <Select 
                value={form.client_id} 
                onValueChange={(v) => setForm(prev => ({ ...prev, client_id: v }))}
              >
                <SelectTrigger className="bg-background" data-testid="project-client-select">
                  <SelectValue placeholder={t('projects.selectClient')} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.client_id} value={c.client_id}>
                      {c.naam}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-foreground">{t('projects.name')} *</Label>
              <Input
                value={form.naam}
                onChange={(e) => setForm(prev => ({ ...prev, naam: e.target.value }))}
                placeholder={t('projects.namePlaceholder')}
                className="bg-background"
                data-testid="project-name-input"
              />
            </div>
            <div>
              <Label className="text-foreground">{t('common.description')}</Label>
              <Textarea
                value={form.beschrijving}
                onChange={(e) => setForm(prev => ({ ...prev, beschrijving: e.target.value }))}
                placeholder={t('projects.descriptionPlaceholder')}
                className="bg-background"
                data-testid="project-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-foreground">{t('projects.hourlyRate')} (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.uurtarief}
                  onChange={(e) => setForm(prev => ({ ...prev, uurtarief: e.target.value }))}
                  placeholder="75.00"
                  className="bg-background"
                  data-testid="project-rate-input"
                />
              </div>
              <div>
                <Label className="text-foreground">{t('projects.budgetHours')}</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={form.budget_uren}
                  onChange={(e) => setForm(prev => ({ ...prev, budget_uren: e.target.value }))}
                  placeholder="100"
                  className="bg-background"
                  data-testid="project-budget-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-foreground">{t('projects.projectActive')}</Label>
              <Switch
                checked={form.actief}
                onCheckedChange={(v) => setForm(prev => ({ ...prev, actief: v }))}
                data-testid="project-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={saveProject} data-testid="save-project-btn">
              {editing ? t('common.save') : t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
